import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

export interface Testimonial {
  name: string;
  title: string;
  content: string;
  avatarUrl: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

export const TestimonialCarousel = ({ testimonials }: TestimonialCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const testimonial = testimonials[currentIndex];

  return (
    <Box sx={{ position: 'relative', maxWidth: 700, mx: 'auto' }}>
      <Card
        sx={{
          textAlign: 'center',
          py: 4,
          px: { xs: 2, md: 6 },
          position: 'relative',
          overflow: 'visible',

        }}
      >
        <FormatQuoteIcon
          sx={{
            position: 'absolute',
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 60,
            color: 'primary.light',
            opacity: 0.3,
          }}
        />
        <CardContent >
          <Typography
            variant="body1"
            sx={{
              fontStyle: 'italic',
              fontSize: { xs: '1rem', md: '1.125rem' },
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            "{testimonial.content}"
          </Typography>
          <Stack alignItems="center" spacing={1}>
            <Avatar
              src={testimonial.avatarUrl}
              alt={testimonial.name}
              sx={{ width: 64, height: 64 }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {testimonial.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {testimonial.title}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {testimonials.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: { xs: -8, md: -48 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: -8, md: -48 },
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {testimonials.length > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          {testimonials.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'action.disabled',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
